import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { IconInitial, IconPhoto } from '../../../assets/icons';
import { EditIconControl, EditIconPage, SubmitButton } from '../../../components';
import { useBackdropContext } from '../../../context/backdrop';
import { RemoveProfileImageMutation } from '../../../data-access/gql-types/graphql';
import { REMOVE_PROFILE_IMAGE } from '../../../data-access/mutations/user';
import { USER_ME } from '../../../data-access/queries/user';
import { useSetProfileImage } from '../hooks/user-profile-image';
import { useUserProfileStore } from '../../../store/user-profile';
import { toastError, toastSuccess } from '../../../utils/toast';
import Avatar from './avatar';
import './index.scss';
import UserInitial from './user-initial';

type EditAvatarProps = {
  setOpen: (open: boolean) => void;
  setUseUserInitial: Dispatch<SetStateAction<boolean>>;
  useUserInitial: boolean;
  profileImageUrlTemp: string | undefined;
  setProfileImageUrlTemp: (value: string | undefined) => void;
  setProfileImageUrl: (value: string | undefined) => void;
};

const EditAvatar: React.FC<EditAvatarProps> = ({
  setOpen,
  useUserInitial,
  setUseUserInitial,
  profileImageUrlTemp,
  setProfileImageUrlTemp,
  setProfileImageUrl,
}) => {
  const { t } = useTranslation('profile');
  const {
    meData: { userInitial },
    refetchUserMe,
  } = useUserProfileStore((s) => s);
  const { setProfileImage } = useSetProfileImage();
  const [isValid, setIsValid] = useState<boolean>(false);
  const [useFile, setUseFile] = useState<File>();
  const [userInitialIsChecked, setUserInitialIsChecked] = useState<boolean>(useUserInitial);
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();

  const [removeProfileImage, { loading }] = useMutation<RemoveProfileImageMutation>(REMOVE_PROFILE_IMAGE, {
    refetchQueries: [USER_ME],
  });

  useEffect(() => {
    if (useFile) {
      const objectUrl = URL.createObjectURL(useFile);
      setProfileImageUrlTemp(objectUrl);
    }
  }, [useFile, setProfileImageUrlTemp]);

  const finishSubmit = (content: string, imageUrl: boolean) => {
    refetchUserMe();
    if (imageUrl) {
      setProfileImageUrl(profileImageUrlTemp);
    } else {
      setProfileImageUrlTemp(undefined);
      setProfileImageUrl(undefined);
    }
    toastSuccess({ content });
    setOpen(false);
    setIsValid(false);
  };

  const onSubmit = () => {
    if (!useFile) {
      turnOnBackdrop();
      removeProfileImage({
        onCompleted: (data) => {
          if (data.removeProfileImage.ok) {
            turnOffBackdrop();
          }
        },
        onError: () => turnOffBackdrop(),
      });
      setUseUserInitial(userInitialIsChecked);
      finishSubmit(
        t(userInitialIsChecked ? 'avatar.toastUserInitialSuccess' : 'avatar.toastRemoveUserInitialSuccess'),
        false,
      );
    }

    if (useFile) {
      setProfileImage(useFile, () => finishSubmit(t('avatar.toastFileSuccess'), true));
    }
  };

  const currentIconControl = useMemo(() => {
    if (userInitialIsChecked) {
      return userInitial ? <IconInitial initial={userInitial} /> : <IconPhoto />;
    }

    return profileImageUrlTemp ? (
      <img
        src={
          profileImageUrlTemp.includes('blob:')
            ? profileImageUrlTemp
            : `${profileImageUrlTemp}?t=${new Date().getTime()}`
        }
        alt="avatar"
        className="edit-avatar__avatar"
      />
    ) : (
      <IconPhoto />
    );
  }, [userInitialIsChecked, profileImageUrlTemp, userInitial]);

  const handleAvatar = (value: File) => {
    if (value.type.includes('image')) {
      setUseFile(value);
      setUserInitialIsChecked(false);
      setIsValid(!!value);
    } else toastError({ content: t('avatar.toastFileError') });
  };

  const handleUserInitial = () => {
    const newValue = !userInitialIsChecked;
    setUserInitialIsChecked(newValue);
    setIsValid(newValue !== useUserInitial);
  };

  return (
    <EditIconPage setOpen={setOpen} title={t('avatar.change')}>
      <EditIconControl caption={t('avatar.current')}>{currentIconControl}</EditIconControl>
      <hr className="m-t-24 m-b-24" />
      <h2>{t('avatar.option')}</h2>
      <div className="grid-list-24">
        <Avatar profileImageUrl={profileImageUrlTemp} setUseFile={handleAvatar} />
        <UserInitial
          setUseUserInitial={handleUserInitial}
          useUserInitial={userInitialIsChecked}
          disabled={!profileImageUrlTemp}
        />
      </div>
      <SubmitButton disabled={!isValid} onClick={onSubmit} isLoading={loading} />
    </EditIconPage>
  );
};
export default EditAvatar;
